import { useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useAccountData from 'src/hooks/useAccountData';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import { BigNum, MarketType, PerpMarketAccount, ZERO } from '@drift-labs/sdk';
import {
	MarketId,
	UIMarket,
	UIOrderType,
	COMMON_UI_UTILS,
} from '@drift/common';
import useMarkPrice from 'src/hooks/useMarkPrice';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import useIsSwiftEnabledForTrade from 'src/hooks/signedMsgOrders/useIsSwiftEnabledForTrade';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import usePreviewOrder from 'src/components/TradeForm/hooks/usePreviewOrder';
import { useOpenPerpOrder } from '../trade/perp/useOpenPerpOrder';
import { getMarginUsedForPosition } from 'src/utils/position';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';
import { PerpTradeFormOutputProps } from 'src/stores/DriftStore/actions/actionHelpers/types';

interface Params {
	marketIndex: number;
	uiOrderType: UIOrderType;
	baseSize: BigNum;
	onSuccess?: () => void;
	shouldCaptureEvent?: boolean;
	limitPrice?: BigNum;
	triggerPrice?: BigNum;
}

export default function useExecuteClosePosition({
	marketIndex,
	uiOrderType,
	baseSize,
	onSuccess,
	shouldCaptureEvent,
	limitPrice,
	triggerPrice,
}: Params) {
	const marketId = new MarketId(marketIndex, MarketType.PERP);
	const uiMarket = UIMarket.createPerpMarket(marketIndex);
	const userAccount = useAccountData();
	const userAccountClient = userAccount?.client;
	const setAccountStore = useDriftAccountsStore((s) => s.set);
	const setDriftStore = useDriftStore((s) => s.set);
	const { captureEvent } = usePostHogCapture();
	const currentMarkPrice = useMarkPrice(marketId);
	const [{ showPnlOnClose, showClosePositionConfirmation }] =
		useCurrentSettings();
	const isSwiftEnabled = useIsSwiftEnabledForTrade({
		marketOracle: uiMarket.market.oracleSource,
		orderType: uiOrderType,
		marketIndex,
	});
	const targetMarketInfo = useMarketsInfoStore((s) =>
		s.getMarketInfoByIndexAndType(marketIndex, MarketType.PERP)
	);
	const openPerpPositions = useCurrentAccountPositions();
	const position = openPerpPositions.find(
		(position) => position.marketIndex === marketIndex
	);
	const [isPending, setIsPending] = useState(false);

	const marginInfo = useCurrentAccountMarginInfo();
	const { leverage, collateral } = marginInfo ?? {
		leverage: 0,
		collateral: ZERO,
	};

	const closeOrderSide = position?.direction === 'long' ? 'sell' : 'buy';
	const markPriceToUse = currentMarkPrice;

	const positionSizeString = baseSize.abs().toNum().toString();
	const isTriggerOrder =
		uiOrderType === 'takeProfitLimit' || uiOrderType === 'stopLimit';

	// Use usePreviewOrder with correct close position overrides
	const previewOrderParams = usePreviewOrder(marketId, {
		baseSizeStringValue: positionSizeString,
		side: closeOrderSide,
		orderType: uiOrderType,
		reduceOnly: true, // Close orders are always reduce-only
	});

	const priceImpactInfo = previewOrderParams.priceImpact;

	const priceInputValue = (limitPrice ?? markPriceToUse).print();
	const triggerPriceInputValue = (triggerPrice ?? markPriceToUse).print();

	const openPerpOrder = useOpenPerpOrder();
	const displayLeverage = useGetDisplayLeverageForMarket(marketIndex);

	const closePosition = async () => {
		if (
			isPending ||
			!position ||
			!userAccountClient ||
			!targetMarketInfo?.account
		) {
			return;
		}

		const currentIndex = position.marketIndex;

		// Validate that we have price impact info
		if (!priceImpactInfo) {
			console.warn('Failed to get price impact info for close position');
			return;
		}

		setAccountStore((state) => {
			const userAccountData = state.accounts[userAccount.userKey];
			if (userAccountData) {
				// Derive leverage from position's maxMarginRatio
				const positionLeverage = position.maxMarginRatio
					? (COMMON_UI_UTILS.convertMarginRatioToLeverage(
							position.maxMarginRatio
						) ?? 1)
					: leverage;

				// Get margin used for this specific position
				const marginUsed = userAccountClient
					? (getMarginUsedForPosition(userAccountClient, marketIndex, false) ??
						null)
					: collateral;

				// Index pnlInfo by market index
				userAccountData.pnlInfo = {
					...userAccountData.pnlInfo,
					[marketIndex]: {
						leverageBeforeClose: positionLeverage,
						collateralBeforeClose: marginUsed,
					},
				};
			}
		});

		setIsPending(true);

		if (shouldCaptureEvent) {
			// Technically this can now be sent from either the `ClosePositionModal` or `TpSlOrderFormPopup`.
			captureEvent('closing_position_from_popup');
		}

		const nextOrderId = userAccountClient.getUserAccount().nextOrderId;

		// Store the orderId BEFORE placing order so the PnL modal listener can find it
		// when order_completely_filled event fires (which may happen during openPerpOrder)
		const pnlModalKey = `${currentIndex}-${nextOrderId}`;
		if (showPnlOnClose) {
			// Check if position is isolated
			const perpPosition = userAccountClient.getPerpPosition(marketIndex);
			const isIsolated = perpPosition
				? userAccountClient.isPerpPositionIsolated(perpPosition)
				: false;

			setDriftStore((s) => {
				s.ordersAwaitingPnlModalDisplay = {
					...s.ordersAwaitingPnlModalDisplay,
					[pnlModalKey]: {
						openPositionInfo: position,
						orderId: nextOrderId,
						isIsolated,
					},
				};
			});
		}

		try {
			const params: PerpTradeFormOutputProps & { leadSide: 'base' | 'quote' } =
				{
					side: closeOrderSide,
					baseSizeStringValue: baseSize.abs().print(),
					leadSide: 'base' as const,
					orderType: uiOrderType,
					priceBoxStringValue: isTriggerOrder
						? triggerPriceInputValue
						: priceInputValue,
					secondaryPriceBoxStringValue: isTriggerOrder
						? priceInputValue
						: undefined,
					reduceOnly: true,
					postOnly: false,
					immediateOrCancel: false,
					slippageTolerance: previewOrderParams.slippage,
					targetMarketIndex: currentIndex,
					priceImpact: priceImpactInfo,
					cancelExistingOrders: false,
					perpMarketAccount: targetMarketInfo.account as PerpMarketAccount,
					isSwiftEnabled,
					displayLeverage,
				};

			const result = await openPerpOrder(params);

			if (result) {
				onSuccess?.();
			} else {
				// Order failed — clean up pnlInfo snapshot
				setAccountStore((state) => {
					const data = state.accounts[userAccount.userKey];
					if (data?.pnlInfo[currentIndex]) {
						delete data.pnlInfo[currentIndex];
					}
				});
				if (showPnlOnClose) {
					setDriftStore((s) => {
						const { [pnlModalKey]: _, ...rest } =
							s.ordersAwaitingPnlModalDisplay;
						s.ordersAwaitingPnlModalDisplay = rest;
					});
				}
			}
		} catch (error) {
			console.error('[useExecuteClosePosition] Error closing position', error);
			// Order failed — clean up pnlInfo snapshot
			setAccountStore((state) => {
				const data = state.accounts[userAccount.userKey];
				if (data?.pnlInfo[currentIndex]) {
					delete data.pnlInfo[currentIndex];
				}
			});
			if (showPnlOnClose) {
				setDriftStore((s) => {
					const { [pnlModalKey]: _, ...rest } = s.ordersAwaitingPnlModalDisplay;
					s.ordersAwaitingPnlModalDisplay = rest;
				});
			}
		} finally {
			setIsPending(false);
		}
	};

	return { closePosition, isPending, showClosePositionConfirmation };
}
